import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../theme/ThemeContext';
import { fetchStaffCredentials } from '../../../services/api/staffApi';
import './StaffCredentialsModal.css';

/**
 * StaffCredentialsModal
 *
 * Displays staff login credentials (username, temp password)
 * with per-row copy and a "Copy All" button.
 *
 * Two modes:
 *   1. credentials prop provided → display those directly (after bulk create)
 *   2. credentials prop empty/omitted → fetch from GET /api/organisation/mem/temp
 *
 * Props:
 *   isOpen       — boolean
 *   credentials  — Array (optional, for inline display after creation)
 *   onClose      — () => void
 */
function StaffCredentialsModal({ isOpen, credentials: propCredentials, onClose }) {
  const { theme } = useTheme();
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [fetched, setFetched] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Decide which credentials to display
  const credentials = (propCredentials && propCredentials.length > 0) ? propCredentials : fetched;

  // Fetch from API when opened with no inline credentials
  useEffect(() => {
    if (!isOpen) return;
    if (propCredentials && propCredentials.length > 0) return; // use prop data

    let cancelled = false;
    const load = async () => {
      setIsFetching(true);
      setFetchError(null);
      const result = await fetchStaffCredentials();
      if (cancelled) return;
      if (result.success) {
        setFetched(result.data);
      } else {
        setFetchError(result.error);
      }
      setIsFetching(false);
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen, propCredentials]);

  // Dismiss on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen, onClose]);

  /**
   * Build a single-row text block for clipboard.
   */
  const formatRow = useCallback((c) => {
    return [
      `Name: ${c.first_name} ${c.last_name}`,
      `Username: ${c.username}`,
      `Phone: ${c.mobile}`,
      `Temporary Password: ${c.temporary_password}`,
    ].join('\n');
  }, []);

  /**
   * Copy one row's credentials.
   */
  const copyOne = async (idx) => {
    try {
      await navigator.clipboard.writeText(formatRow(credentials[idx]));
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch {
      /* fallback: ignore */
    }
  };

  /**
   * Copy ALL rows' credentials separated by a blank line.
   */
  const copyAll = async () => {
    try {
      const text = credentials.map(formatRow).join('\n\n---\n\n');
      await navigator.clipboard.writeText(text);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch {
      /* fallback: ignore */
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="scm-overlay"
        onClick={onClose}
        style={{ backgroundColor: theme.background.overlay || 'rgba(0,0,0,0.5)' }}
      />
      <div
        className="scm-modal"
        style={{ backgroundColor: theme.background.primary, color: theme.text.primary }}
      >
        {/* Header */}
        <div className="scm-header">
          <div>
            <h2 className="scm-title">Staff Login Credentials</h2>
            <p className="scm-subtitle" style={{ color: theme.text.secondary }}>
              Share these credentials with your staff. Temporary passwords must be changed on first login.
            </p>
          </div>
          <button
            className="scm-close-btn"
            onClick={onClose}
            style={{ color: theme.text.secondary }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div className="scm-body">
          {/* Loading state */}
          {isFetching && (
            <div className="scm-loading" style={{ color: theme.text.secondary }}>
              <span className="staff-spinner" style={{ borderTopColor: theme.brand.purple }} />
              Loading credentials...
            </div>
          )}

          {/* Fetch error */}
          {fetchError && !isFetching && (
            <div className="scm-error" style={{ color: theme.brand.error }}>
              <p>{fetchError}</p>
            </div>
          )}

          {/* Empty state */}
          {!isFetching && !fetchError && credentials.length === 0 && (
            <div className="scm-empty" style={{ color: theme.text.secondary }}>
              <p>No staff credentials available.</p>
            </div>
          )}

          {credentials.map((c, idx) => (
            <div
              key={c.member_id || idx}
              className="scm-card"
              style={{ borderColor: theme.border.default }}
            >
              <div className="scm-card-body">
                <div className="scm-field">
                  <span className="scm-label" style={{ color: theme.text.secondary }}>Name</span>
                  <span className="scm-value">{c.first_name} {c.last_name}</span>
                </div>
                <div className="scm-field">
                  <span className="scm-label" style={{ color: theme.text.secondary }}>Username</span>
                  <span className="scm-value scm-mono">{c.username}</span>
                </div>
                <div className="scm-field">
                  <span className="scm-label" style={{ color: theme.text.secondary }}>Phone</span>
                  <span className="scm-value">{c.mobile}</span>
                </div>
                <div className="scm-field">
                  <span className="scm-label" style={{ color: theme.text.secondary }}>Temporary Password</span>
                  <span className="scm-value scm-mono scm-password">{c.temporary_password}</span>
                </div>
              </div>
              <button
                className="scm-copy-btn"
                onClick={() => copyOne(idx)}
                style={{
                  color: copiedIdx === idx ? '#22c55e' : theme.brand.purple,
                  borderColor: copiedIdx === idx ? '#22c55e' : theme.brand.purple,
                }}
              >
                {copiedIdx === idx ? '✓ Copied' : '📋 Copy'}
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="scm-footer">
          {credentials.length > 0 && (
            <button
              className="scm-copy-all-btn"
              onClick={copyAll}
              style={{
                background: copiedAll ? '#22c55e' : theme.gradient.userMessage,
                color: '#fff',
              }}
            >
              {copiedAll ? '✓ All Copied!' : '📋 Copy All'}
            </button>
          )}
          <button
            className="scm-done-btn"
            onClick={onClose}
            style={{ color: theme.text.secondary }}
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
}

export default StaffCredentialsModal;
